/**
 * Typed client for the Communications module (email/SMS, templates, automation rules).
 *
 * Routes (beam-backend commsRouter.ts, /api, Bearer):
 *   GET  /api/communications                 → CommunicationRow[]  (message log)
 *   POST /api/communications/send-email       { to, subject, body, contactId?, purpose? } (owner/office only)
 *   POST /api/communications/send-sms         { to, body, contactId?, purpose? }          (owner/office only)
 *   GET  /api/message-templates               → TemplateRow[]
 *   POST /api/message-templates               { name, channel, subject?, body, purpose? }
 *   GET  /api/automation-rules                → AutomationRuleRow[]
 *   POST /api/automation-rules                { name, trigger_type, channel, template_id, phase_name?, timing_offset?, quiet_hours? }
 *
 * New comms endpoints return raw Prisma rows (snake_case); we type them locally rather than adding a
 * zod DTO for every shape. Bearer token via client. NEVER imports @smartbox/* (mobile invariant 1).
 */
import { get, post } from './client';

export type Channel = 'email' | 'sms';
export type Purpose =
  | 'generic'
  | 'marketing'
  | 'receipt'
  | 'estimate_followup'
  | 'next_phase_follow_up';

// ---------------------------------------------------------------------------
// Row shapes (raw snake_case from the backend)
// ---------------------------------------------------------------------------

export interface CommunicationRow {
  id: string;
  direction: string;
  channel: string;
  body: string | null;
  subject: string | null;
  to_address: string | null;
  status: string;
  purpose: string | null;
  created_at: string;
}

export interface TemplateRow {
  id: string;
  name: string;
  channel: string;
  subject: string | null;
  body: string;
  purpose: string | null;
  created_at: string;
}

export interface AutomationRuleRow {
  id: string;
  name: string;
  trigger_type: string;
  channel: string;
  template_id: string;
  phase_name: string | null;
  timing_offset: string | null;
  quiet_hours: boolean | null;
  created_at: string;
}

/** Result envelope from a send (sent / queued / skipped + optional reason). */
export interface SendResult {
  id?: string;
  status: string;
  reason?: string | null;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface SendEmailRequest {
  to: string;
  subject: string;
  body: string;
  contactId?: string;
  purpose?: Purpose;
}

export interface SendSmsRequest {
  to: string;
  body: string;
  contactId?: string;
  purpose?: Purpose;
}

export interface CreateTemplateRequest {
  name: string;
  channel: Channel;
  subject?: string;
  body: string;
  purpose?: Purpose;
}

export interface CreateAutomationRuleRequest {
  name: string;
  trigger_type: string;
  channel: Channel;
  template_id: string;
  phase_name?: string;
  timing_offset?: string;
  quiet_hours?: boolean;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** Recent message log (sent + received communications). */
export async function listCommunications(): Promise<CommunicationRow[]> {
  return get<CommunicationRow[]>('/api/communications');
}

/** Send an email (owner/office only — server enforces; surfaces a forbidden error otherwise). */
export async function sendEmail(body: SendEmailRequest): Promise<SendResult> {
  return post<SendResult>('/api/communications/send-email', body);
}

/** Send an SMS (owner/office only). */
export async function sendSms(body: SendSmsRequest): Promise<SendResult> {
  return post<SendResult>('/api/communications/send-sms', body);
}

/** List message templates. */
export async function listTemplates(): Promise<TemplateRow[]> {
  return get<TemplateRow[]>('/api/message-templates');
}

/** Create a message template. */
export async function createTemplate(body: CreateTemplateRequest): Promise<TemplateRow> {
  return post<TemplateRow>('/api/message-templates', body);
}

/** List automation rules. */
export async function listAutomationRules(): Promise<AutomationRuleRow[]> {
  return get<AutomationRuleRow[]>('/api/automation-rules');
}

/** Create an automation rule. */
export async function createAutomationRule(
  body: CreateAutomationRuleRequest,
): Promise<AutomationRuleRow> {
  return post<AutomationRuleRow>('/api/automation-rules', body);
}
